import { Fragment, useMemo, useState } from "react";
import { Listbox, Transition } from "@headlessui/react";
import {
  MagnifyingGlassIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  FunnelIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { votersAPI } from "../../services/api";
import { showSuccess, showError } from "../../utils/notificationHelper";

import Table from "../common/Table";

function VotersTable({
  voters,
  setVoters,
  fetchVoters,
  ageGroupFilter,
  onAgeGroupChange,
  searchTerm,
  onSearchChange,
  precinctNameFilter,
  onPrecinctNameChange,
  statusFilter,
  onStatusChange,
  precincts,
  isLoading = false,
  pagination,
  onPageChange,
}) {
  const [editRowId, setEditRowId] = useState(null);
  const [editPrecinct, setEditPrecinct] = useState("");
  const [editPrecinctSearch, setEditPrecinctSearch] = useState("");
  const [togglingVoter, setTogglingVoter] = useState(null);

  const [filterOpen, setFilterOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);

  // All filtering is handled by the backend API, so we just use the voters as-is
  const filtered = voters;

  const toggleVoterRegistration = async (id) => {
    setTogglingVoter(id);
    try {
      const voter = voters.find((v) => v.id === id);
      if (!voter) return;

      const newStatus = voter.is_register_voter === 1 ? 0 : 1;
      const statusText = newStatus === 1 ? "Registered Voter" : "Non-Registered Voter";
      
      const res = await votersAPI.patch(id, {
        is_register_voter: newStatus,
      });

      if (res.data?.success) {
        setVoters((prev) =>
          prev.map((v) =>
            v.id === id
              ? { ...v, is_register_voter: newStatus }
              : v,
          ),
        );
        
        showSuccess(
          `Voter status successfully updated to ${statusText}`,
          { duration: 3000 }
        );
      } else {
        console.warn("Backend update failed:", res.data);
        showError(
          "Failed to update voter registration status. Please try again.",
          { duration: 4000 }
        );
      }
    } catch (err) {
      console.error("Error updating voter registration:", err);
      showError(
        "An error occurred while updating voter registration. Please try again.",
        { duration: 4000 }
      );
    } finally {
      setTogglingVoter(null);
    }
  };

  const handleEdit = (voter) => {
    setEditRowId(voter.id);
    setEditPrecinct(voter.precinct_id != null ? String(voter.precinct_id) : "");
    setEditPrecinctSearch("");
  };

  // Simplified handleSave
  const handleSave = async (id) => {
    const voter = voters.find((v) => v.id === id);
    if (!voter) return;

    if (!editPrecinct) return;

    const selectedPrecinct = precinctOptions.find(
      (p) => String(p.value) === String(editPrecinct),
    );

    try {
      const res = await votersAPI.patch(id, {
        precinct_id: editPrecinct,
      });

      if (res.data?.success) {
        const updated = res.data.data.voter;

        setVoters((prev) =>
          prev.map((v) =>
            v.id === id
              ? {
                ...v,
                precinct:
                  updated?.precinct?.precinct_name ||
                  selectedPrecinct?.label ||
                  v.precinct,
                precinct_id:
                  updated?.precinct?.precinct_id != null
                    ? updated.precinct.precinct_id
                    : v.precinct_id,
                status: updated.status || v.status,
                ageGroup: updated.age_group
                  ? [updated.age_group]
                  : v.ageGroup,
              }
              : v,
          ),
        );

        // re-fetch from backend for accuracy
        await fetchVoters();
      } else {
        console.warn("Backend update failed:", res.data);
      }

      setEditRowId(null);
      setEditPrecinct("");
      setEditPrecinctSearch("");
    } catch (err) {
      console.error("Error updating precinct:", err);
    }
  };

  const handleCancel = () => {
    setEditRowId(null);
    setEditPrecinct("");
    setEditPrecinctSearch("");
  };

  const precinctOptions = useMemo(() => {
    const opts = (precincts || [])
      .map((p) => {
        const label = p.name || p.precinct_name || "";
        const value = p.precinct_id ?? p.id;
        return {
          label,
          value: value != null ? String(value) : "",
        };
      })
      .filter((p) => p.label && p.value);

    opts.sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { numeric: true }),
    );
    return opts;
  }, [precincts]);

  const filteredEditPrecinctOptions = useMemo(() => {
    const q = editPrecinctSearch.trim().toLowerCase();
    if (!q) return precinctOptions;
    return precinctOptions.filter((p) => p.label.toLowerCase().includes(q));
  }, [editPrecinctSearch, precinctOptions]);

  const columns = [
    {
      key: "voter",
      label: "Voter",
      render: (_, v) => (
        <div className="flex items-center">
          <div className="h-12 w-12 rounded-full bg-gradient-to-r from-blue-400 to-blue-600 flex items-center justify-center text-white font-semibold text-sm hover:scale-110 transition-transform duration-200">
            {v.name
              .split(" ")
              .map((n) => n[0])
              .join("")
              .slice(0, 2)}
          </div>
          <div className="ml-3">
            <p className="font-medium text-gray-900 text-sm">{v.name}</p>
            <p className="text-xs text-gray-500 flex items-center gap-2">
              <span className="font-mono text-xs bg-blue-100 text-blue-800 font-semibold px-2 py-0.5 rounded">
                {v.votersIdNo}
              </span>
              <span>• {v.contact_number}</span>
            </p>
          </div>
        </div>
      ),
    },
    {
      key: "registration",
      label: "Registered Voter",
      render: (_, v) => (
        <div className="flex items-center gap-3">
          <button
            onClick={() => toggleVoterRegistration(v.id)}
            disabled={togglingVoter === v.id}
            className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors duration-200 ${
              v.is_register_voter === 1 ? "bg-blue-600" : "bg-gray-300"
            } ${togglingVoter === v.id ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                v.is_register_voter === 1 ? "translate-x-5" : "translate-x-1"
              }`}
            />
          </button>
          <span className="text-xs text-blue-600">
            {v.is_register_voter === 1 ? "Registered Voter" : "Non-Registered Voter"}
          </span>
        </div>
      ),
    },
    {
      key: "precinct",
      label: "Precinct",
      render: (_, v) =>
        editRowId === v.id ? (
          <div className="relative">
            <Listbox
              value={editPrecinct}
              onChange={(val) => {
                setEditPrecinct(val);
                setOpenDropdown(null);
                setEditPrecinctSearch("");
              }}
            >
              {() => (
                <div className="relative">
                  <Listbox.Button
                    onClick={() =>
                      setOpenDropdown((prev) =>
                        prev === "precinct-edit" ? null : "precinct-edit",
                      )
                    }
                    className="relative w-full cursor-pointer rounded border border-gray-300 bg-white py-1.5 pl-3 pr-8 text-left text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <span className="block truncate">
                      {editPrecinct
                        ? precinctOptions.find(
                          (p) => String(p.value) === String(editPrecinct),
                        )?.label || "Select Precinct"
                        : "Select Precinct"}
                    </span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                      {openDropdown === "precinct-edit" ? (
                        <ChevronUpIcon className="h-4 w-4 text-gray-500" />
                      ) : (
                        <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                      )}
                    </span>
                  </Listbox.Button>

                  <Transition
                    as={Fragment}
                    show={openDropdown === "precinct-edit"}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100 scale-100"
                    leaveTo="opacity-0 scale-95"
                  >
                    <Listbox.Options className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black/10 focus:outline-none">
                      <div className="px-2 pb-2">
                        <input
                          value={editPrecinctSearch}
                          onChange={(e) =>
                            setEditPrecinctSearch(e.target.value)
                          }
                          placeholder="Search precinct..."
                          className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                      {filteredEditPrecinctOptions.length > 0 ? (
                        filteredEditPrecinctOptions.map((p) => (
                          <Listbox.Option
                            key={p.value}
                            value={p.value}
                            className={({ active }) =>
                              `relative cursor-pointer select-none py-2 pl-3 pr-3 ${active
                                ? "bg-blue-100 text-blue-900"
                                : "text-gray-900"
                              }`
                            }
                          >
                            {({ selected }) => (
                              <span
                                className={`block truncate ${selected ? "font-semibold" : "font-normal"
                                  }`}
                              >
                                {p.label}
                              </span>
                            )}
                          </Listbox.Option>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-gray-500">
                          No precincts found.
                        </div>
                      )}
                    </Listbox.Options>
                  </Transition>
                </div>
              )}
            </Listbox>
          </div>
        ) : (
          <span className="text-sm text-gray-900">{v.precinct}</span>
        ),
      },
      {
        key: "status",
        label: "Status",
        render: (_, v) => (
          <span className="bg-green-100 text-green-800 font-semibold px-2 py-1 rounded-full text-xs">
            {v.status}
          </span>
        ),
      },
      {
        key: "ageGroup",
        label: "Age Group",
        render: (_, v) =>
          v.ageGroup && v.ageGroup.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {v.ageGroup.map((tag, idx) => (
                <span
                  key={idx}
                  className="text-xs font-medium px-2 rounded-full border border-gray-300 text-gray-800"
                >
                  {tag}
                </span>
              ))}
            </div>
          ) : (
            <span className="text-xs text-gray-500">—</span>
          ),
      },
      {
        key: "actions",
        label: "Actions",
        render: (_, v) => (
          <div className="flex justify-end gap-2">
            {editRowId === v.id ? (
              <>
                <button
                  onClick={() => handleSave(v.id)}
                  className="flex items-center justify-center w-8 h-8 rounded-full text-green-600 hover:bg-green-200 transition"
                  >
                  <CheckIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={handleCancel}
                  className="flex items-center justify-center w-8 h-8 rounded-full text-red-600 hover:bg-red-200 transition"
                  >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </>
            ) : (
              <button
              onClick={() => handleEdit(v)}
              className="flex items-center justify-center w-8 h-8 rounded-full text-yellow-600 hover:bg-yellow-100 transition"
              >
              <PencilIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      ),
    },
  ];
  
  return (
    <Table
      columns={columns}
      data={filtered}
      getRowKey={(v) => v.id}
      header={
        <div>
          {/* Search & Filter */}
          <div className="flex justify-between items-center pb-3">
            <h2 className="font-semibold text-gray-800 flex items-center gap-2">
              Voters
            </h2>
            <div className="flex items-center gap-2">
              <div className="relative w-64">
                <input
                  type="text"
                  placeholder="Search voters..."
                  value={searchTerm}
                  onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              {/* Filter Button */}
              <button
                onClick={() => setFilterOpen((prev) => !prev)}
                className={`flex items-center gap-1 px-5 py-2 border rounded-lg text-sm transition
                ${filterOpen
                    ? "border-blue-500 text-blue-600"
                    : "border-gray-400 text-gray-600 hover:bg-gray-50"
                  }`}
              >
                <FunnelIcon
                  className={`h-5 w-5 ${filterOpen ? "text-blue-600" : "text-gray-500"
                    }`}
                />
                <span>Filter</span>
              </button>
            </div>
          </div>

          {/* Filter dropdowns */}
          {filterOpen && (
            <div className="pb-3 flex justify-center gap-8 text-sm">
              {/* Status Select */}
              <div className="flex flex-col flex-1 relative">
                <label className="text-sm font-semibold text-gray-800 mb-1">
                  Status
                </label>
                <div className="relative">
                  <select
                    value={statusFilter || "All Status"}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Only set status filter if it's at least 3 characters or "All Status"
                      if (onStatusChange) {
                        if (value === "All Status") {
                          onStatusChange(null);
                        } else if (value.length >= 3) {
                          onStatusChange(value);
                        }
                      }
                      setOpenDropdown(null);
                    }}
                    onClick={() =>
                      setOpenDropdown((prev) =>
                        prev === "status" ? null : "status",
                      )
                    }
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm 
                         focus:border-blue-500 focus:ring-blue-500 appearance-none pr-8"
                  >
                    <option>All Status</option>
                    <option>Active</option>
                    <option>Inactive</option>
                    <option>Registered</option>
                  </select>
                  {openDropdown === "status" ? (
                    <ChevronUpIcon className="w-4 h-4 text-gray-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  ) : (
                    <ChevronDownIcon className="w-4 h-4 text-gray-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  )}
                </div>
              </div>

              {/* Precinct Select */}
              <div className="flex flex-col flex-1 relative">
                <label className="text-sm font-semibold text-gray-800 mb-1">
                  Precinct
                </label>
                <div className="relative">
                  <select
                    value={precinctNameFilter || "All Precinct"}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (onPrecinctNameChange) {
                        onPrecinctNameChange(
                          value === "All Precinct" ? null : value,
                        );
                      }
                      setOpenDropdown(null);
                    }}
                    onClick={() =>
                      setOpenDropdown((prev) =>
                        prev === "precinct" ? null : "precinct",
                      )
                    }
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm 
                         focus:border-blue-500 focus:ring-blue-500 appearance-none pr-8"
                  >
                    <option>All Precinct</option>
                    {precinctOptions.map((p) => (
                      <option key={p.value} value={p.label}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                  {openDropdown === "precinct" ? (
                    <ChevronUpIcon className="w-4 h-4 text-gray-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  ) : (
                    <ChevronDownIcon className="w-4 h-4 text-gray-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  )}
                </div>
              </div>

              {/* Age Group Select */}
              <div className="flex flex-col flex-1 relative">
                <label className="text-sm font-semibold text-gray-800 mb-1">
                  Age Group
                </label>
                <div className="relative">
                  <select
                    value={ageGroupFilter || "All Age Group"}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Update parent component's filter state
                      if (onAgeGroupChange) {
                        onAgeGroupChange(value === "All Age Group" ? null : value);
                      }
                      setOpenDropdown(null);
                    }}
                    onClick={() =>
                      setOpenDropdown((prev) => (prev === "age" ? null : "age"))
                    }
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm 
                         focus:border-blue-500 focus:ring-blue-500 appearance-none pr-8"
                  >
                    <option >All Age Group</option>
                    <option value="youth">Youth</option>
                    <option value="early_adult">Early Adult</option>
                    <option value="regular_voter">Regular Voter</option>
                    <option value="senior">Senior</option>
                    <option value="pwd">Pwd</option>
                    <option value="illiterate">Illiterate</option>
                  </select>
                  {openDropdown === "age" ? (
                    <ChevronUpIcon className="w-4 h-4 text-gray-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  ) : (
                    <ChevronDownIcon className="w-4 h-4 text-gray-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      }
      pagination={pagination}
      onPageChange={onPageChange}
      paginationProps={{
        isLoading,
        showSizeSelector: true,
      }}
    />
  );
}

export default VotersTable;
